/* eslint-disable @typescript-eslint/no-explicit-any */
import { zodResolver } from "@hookform/resolvers/zod";
import React, { Typography, AlertTitle, Alert, Grid, TextField, Autocomplete, Paper } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { FormTextInput, SubmitButton } from "components";
import { PartnerPlacement } from "enums";
import { useAuth, usePartners, useRegisterUserForm } from "hooks";
import { Controller, FieldErrors, FormProvider, useForm } from "react-hook-form";
import { PartnerService } from "services";
import { z } from "zod";

export const PartnerForm = () => {
    const { partners } = usePartners('partners-dropdown');
    const { schema } = useRegisterUserForm();
    const { currentUserId } = useAuth();
    const queryClient = useQueryClient();
    type FormFields = z.infer<typeof schema>;
    const methods = useForm<FormFields>({
        resolver: zodResolver(schema),
        mode: 'onBlur',
        defaultValues: {
            placementPartnerId: currentUserId,
            mentorId: currentUserId
        }
    });
    const placementValues = Object.keys(PartnerPlacement)
        .filter(key => isNaN(Number(key)))
        .map((value, index) => {
            return { value: index, text: value };
        });
    const onSubmit = async (data: FormFields) => {
        try {
            await PartnerService.registerUser(data);
            await queryClient.invalidateQueries({ queryKey: ['partners'] });
        } catch (e: any) {
            methods.setError('root', { type: 'manual', message: e.response?.data });
        }
    };
    const onError = async (data: FieldErrors<FormFields>) => {
        console.log(data);
    };
    if (!partners) return <></>
    return (
        <FormProvider {...methods}>
            <form onSubmit={methods.handleSubmit(onSubmit, onError)}>
                <Grid container justifyContent='center' textAlign={'center'} spacing={2} component={Paper} p={5}>
                    {methods.formState?.errors?.root?.message && (
                        <Grid item xs={12} textAlign={'start'}>
                            <Alert severity='error' sx={{ maxWidth: '100%' }} >
                                <AlertTitle>Error</AlertTitle>
                                <Typography variant='subtitle2' gutterBottom>
                                    {methods.formState.errors.root?.message}
                                </Typography>
                            </Alert>
                        </Grid>
                    )}
                    <Grid item xs={12}>
                        <FormTextInput name='email' />
                    </Grid>
                    <Grid item xs={6}>
                        <FormTextInput name='firstName' />
                    </Grid>
                    <Grid item xs={6}>
                        <FormTextInput name='lastName' />
                    </Grid>
                    <Grid item xs={12}>
                        <Controller
                            name='placementPartnerId'
                            control={methods.control}
                            render={({ field }) => (
                                <Autocomplete
                                    value={partners?.find(partner => partner.id === field.value) || null}
                                    onChange={(_, value) => field.onChange(value?.id ?? 0)}
                                    options={partners}
                                    fullWidth
                                    getOptionLabel={(option) => option.firstName + ' ' + option.lastName}
                                    isOptionEqualToValue={(option) => option?.id === field?.value}
                                    renderInput={params => <TextField label='Partner' required error={!!methods.formState.errors.placementPartnerId} helperText={methods.formState.errors.placementPartnerId ? methods.formState.errors.placementPartnerId.message : ''} {...params} size='small' />}
                                />
                            )}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Controller
                            name='mentorId'
                            control={methods.control}
                            render={({ field }) => (
                                <Autocomplete
                                    value={partners?.find(partner => partner.id === field.value) || null}
                                    onChange={(_, value) => field.onChange(value?.id ?? 0)}
                                    options={partners}
                                    fullWidth
                                    getOptionLabel={(option) => option.firstName + ' ' + option.lastName}
                                    isOptionEqualToValue={(option) => option?.id === field?.value}
                                    renderInput={params => <TextField label='Partner' required error={!!methods.formState.errors.mentorId} helperText={methods.formState.errors.mentorId ? methods.formState.errors.mentorId.message : ''} {...params} size='small' />}
                                />
                            )}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Controller
                            name='placement'
                            control={methods.control}
                            render={({ field }) => (
                                <Autocomplete
                                    value={placementValues.find((value) => value.value == field.value) ?? placementValues[0]}
                                    onChange={(_, value) => field.onChange(value?.value ?? 0)}
                                    options={placementValues}
                                    fullWidth
                                    getOptionLabel={(option) => option.text}
                                    isOptionEqualToValue={(option) => option?.value === field?.value}
                                    renderInput={params => <TextField label='Placement' required error={!!methods.formState.errors.placement} helperText={methods.formState.errors.placement ? methods.formState.errors.placement.message : ''} {...params} size='small' />}
                                />
                            )}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <FormTextInput name='city' />
                    </Grid>
                    <Grid item xs={6}>
                        <FormTextInput name='zipCode' />
                    </Grid>
                    <Grid item xs={6}>
                        <FormTextInput name='country' />
                    </Grid>
                    <Grid item xs={6}>
                        <FormTextInput name='phoneNumber' />
                    </Grid>
                    <Grid item xs={6}>
                        <FormTextInput name='password' type='password' />
                    </Grid>
                    <Grid item xs={6}>
                        <FormTextInput name='confirmPassword' type='password' />
                    </Grid>
                    <Grid item xs={12}>
                        <SubmitButton text='Register' />
                    </Grid>
                </Grid>
            </form>
        </FormProvider>
    );
}